import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { sortBy } from 'webapp/utils/common';

import BoardsPopupBoardToObjectBaseComponent from 'webapp/components/boards/popup-board-to-object-base';
import type { Args } from 'webapp/components/popup/inner/modal';

import BoardLoop from 'webapp/models-custom/board-loop';

import type { TrackingOriginType } from 'webapp/services/model-manager/board-loops';

export default class BoardLoopsPopupBoardToLoopComponent extends BoardsPopupBoardToObjectBaseComponent {
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('popup') declare popupService: Service.Popup;

	override popupTargetObject = 'loop';
	override popupIcon: IconName = 'Loop';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectName = this.popupTargetObject.toLowerCase();
		this.selectPlaceholder = `Select ${this.popupTargetObject}`;

		void this.mmBoardLoopsService.requestBoardLoops().then(() => {
			this.selectedId = this.options[0]?.value || '';
		});
	}

	override get options() {
		const options = sortBy(this.mmBoardLoopsService.normalBoardLoopModels, 'id').map((item) => {
			const model = this.model,
				isModelArray = Array.isArray(model),
				isAlreadyUsedInLoop = isModelArray
					? model.every((board) => item.boardIds.includes(board.numericId!))
					: !!model && item.boardIds.includes(model.numericId!),
				canCurrentUserEdit = item.canCurrentUserEdit,
				isDisabled = !canCurrentUserEdit || isAlreadyUsedInLoop,
				boardCount = item.boardCount;

			return {
				value: item.id!,
				label: item.title,
				label2: `${boardCount} dashboard${boardCount === 1 ? '' : 's'}`,
				label3: this.getStatusIcon(canCurrentUserEdit, item.favorite, isAlreadyUsedInLoop),
				avatar: item.imageMediumOrDefault,
				bgTheme: item.bgTheme,
				color: item.color,
				disabled: isDisabled,
			};
		});

		return options;
	}

	override get actionOptions() {
		return [
			{
				id: null,
				text: 'New loop',
				html: '<a class="loop-add">New loop</a>',
				isSpecial: true,
				position: 'top',
				onClick: () => {
					const model = this.model,
						boards = [];

					if (Array.isArray(model)) {
						boards.push(...model);
					} else if (model) {
						boards.push(model);
					}

					const boardIds = boards
						.map((board) => board.numericId)
						.filter((numericId) => typeof numericId === 'number');

					const boardLoop = this.mmBoardLoopsService.createNewModel({
						boardIds,
					});

					this.mmBoardLoopsService.openEditPopup(
						boardLoop,
						this.popupData.trackingOrigin as TrackingOriginType,
					);
				},
			},
		];
	}

	get numberOfBoardsToAdd(): number {
		const model = this.model;

		return Array.isArray(model) ? model.length : model ? 1 : 0;
	}

	override get isSaveDisabled(): boolean {
		const boardLoop = this.mmBoardLoopsService.normalBoardLoopModels.find(
				(item) => item.id === this.selectedId,
			),
			boardCount = boardLoop?.boardCount || 0;

		return boardCount + this.numberOfBoardsToAdd > BoardLoop.maxBoardsInLoop;
	}

	override get saveTooltipText(): string {
		return this.isSaveDisabled
			? `Maximum number of dashboards allowed in the loop is ${BoardLoop.maxBoardsInLoop}.`
			: '';
	}
}
